import { ReactNode } from "react";
import { Helmet } from "react-helmet";
import { StructuredData } from "./StructuredData";
import { SEO_BASE_URL } from "@/lib/seoConfig";

const DEFAULT_OG_IMAGE = `${SEO_BASE_URL}/og-image.png`;

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  /** Path used for og:url only. Canonical tags are owned by RouteSEO. */
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: Array<{
    type: "organization" | "website" | "breadcrumb" | "product" | "faq";
    data?: Record<string, unknown>;
  }>;
  noIndex?: boolean;
  children?: ReactNode;
}

/**
 * Renders SEO meta tags and structured data (Helmet).
 * Does not emit rel=canonical — RouteSEO is the single canonical owner.
 */
export const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  structuredData,
  noIndex = false,
  children,
}: SEOProps) => {
  const absoluteOgImage = ogImage.startsWith("http") ? ogImage : `${SEO_BASE_URL}${ogImage}`;
  let ogUrl = SEO_BASE_URL;
  if (canonical) {
    ogUrl = canonical.startsWith("http") ? canonical : `${SEO_BASE_URL}${canonical}`;
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}

      {noIndex ? <meta name="robots" content="noindex, follow" /> : null}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="TellerWand" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tellerwand" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />

      <meta name="author" content="TellerWand" />
      <meta name="theme-color" content="#0f172a" />

      {structuredData?.map((data) => (
        <StructuredData key={data.type} type={data.type} data={data.data} />
      ))}

      {children}
    </Helmet>
  );
};

export default SEO;
